import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { shared } from '../styles/shared.js';
import { LocaleController } from '../i18n/locale-controller.js';
import { t } from '../i18n/t.js';
import { getAudioService } from '../audio/context.js';
import './app-router.js';
import './parent-gate.js';
import './parent-settings.js';
import './rotate-overlay.js';

@customElement('app-root')
export class AppRoot extends LitElement {
  static override styles = [
    shared,
    css`
      :host {
        display: block;
        position: relative;
        height: 100%;
        width: 100%;
      }

      .splash {
        z-index: 300;
        background: var(--color-accent-yellow);
      }

      .splash__cta {
        font-size: var(--type-xxl);
        margin: 0;
      }

      @media (max-height: 500px) {
        .splash__cta {
          font-size: var(--type-l);
        }
      }

      .gear {
        position: fixed;
        top: var(--space-3);
        left: var(--space-3);
        z-index: 50;
      }

      .gear svg {
        width: 36px;
        height: 36px;
      }
    `,
  ];

  private locale = new LocaleController(this);
  @state() private unlocked = false;
  @state() private gateOpen = false;
  @state() private settingsOpen = false;

  private onSplashTap(): void {
    this.unlocked = true;
    try {
      getAudioService().unlock();
    } catch {
      // Audio service may not be initialised in some test contexts.
    }
    this.dispatchEvent(new CustomEvent('audio-unlock', { bubbles: true, composed: true }));
  }

  private onGearTap(): void {
    this.gateOpen = true;
  }

  private onGateSuccess(): void {
    this.gateOpen = false;
    this.settingsOpen = true;
  }

  private onGateCancel(): void {
    this.gateOpen = false;
  }

  private onSettingsClose(): void {
    this.settingsOpen = false;
  }

  override render() {
    if (!this.unlocked) {
      return html`<div
        class="overlay splash"
        @click=${this.onSplashTap}
        role="button"
        tabindex="0"
        data-testid="splash"
      >
        <p class="splash__cta text-display">${t(this.locale.locale, 'splash.tap')}</p>
      </div>`;
    }
    return html`<rotate-overlay></rotate-overlay>
      <button
        class="gear button-quiet"
        data-testid="gear"
        aria-label="settings"
        @click=${this.onGearTap}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="3"></circle>
          <path
            d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
          ></path>
        </svg>
      </button>
      <main>
        <app-router></app-router>
      </main>
      <parent-gate
        .open=${this.gateOpen}
        @gate-success=${this.onGateSuccess}
        @gate-cancel=${this.onGateCancel}
      ></parent-gate>
      <parent-settings
        .open=${this.settingsOpen}
        @settings-close=${this.onSettingsClose}
      ></parent-settings>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-root': AppRoot;
  }
}
